import React, { useEffect, useMemo, useState } from 'react';
import { Copy, KeyRound, Lock, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { ScrollArea } from '../../ui/scroll-area';
import type { BrowserSavedPassword } from './types';

const getHostname = (value: string | null) => {
  if (!value) {
    return 'current site';
  }

  try {
    return new URL(value).hostname;
  } catch {
    return value;
  }
};

export const BrowserPasswordManagerDialog = ({
  open,
  onOpenChange,
  currentOrigin,
  hasPasswordVault,
  isPasswordVaultUnlocked,
  savedPasswords,
  currentSitePasswords,
  onCreateVault,
  onUnlockVault,
  onLockVault,
  onSavePassword,
  onDeletePassword,
  onFillPassword,
  portalContainer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentOrigin: string | null;
  hasPasswordVault: boolean;
  isPasswordVaultUnlocked: boolean;
  savedPasswords: BrowserSavedPassword[];
  currentSitePasswords: BrowserSavedPassword[];
  onCreateVault: (masterPassword: string) => Promise<void>;
  onUnlockVault: (masterPassword: string) => Promise<void>;
  onLockVault: () => void;
  onSavePassword: (payload: { origin: string; username: string; password: string; label?: string }) => Promise<void>;
  onDeletePassword: (passwordId: string) => Promise<void>;
  onFillPassword: (entry: BrowserSavedPassword) => Promise<boolean>;
  portalContainer?: HTMLElement | null;
}) => {
  const [setupPassword, setSetupPassword] = useState('');
  const [setupPasswordConfirm, setSetupPasswordConfirm] = useState('');
  const [unlockPassword, setUnlockPassword] = useState('');
  const [label, setLabel] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setStatusMessage(null);
      setErrorMessage(null);
      setBusyAction(null);
      setUnlockPassword('');
      setPassword('');
      return;
    }

    setLabel(currentOrigin ? getHostname(currentOrigin) : '');
  }, [currentOrigin, open]);

  const sortedPasswords = useMemo(
    () => [...savedPasswords].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)),
    [savedPasswords]
  );

  const handleCreateVault = async () => {
    setErrorMessage(null);
    setStatusMessage(null);

    if (!setupPassword || setupPassword.length < 8) {
      setErrorMessage('Choose a master password with at least 8 characters.');
      return;
    }

    if (setupPassword !== setupPasswordConfirm) {
      setErrorMessage('Your confirmation password does not match.');
      return;
    }

    setBusyAction('create-vault');
    try {
      await onCreateVault(setupPassword);
      setSetupPassword('');
      setSetupPasswordConfirm('');
      setStatusMessage('Password vault created and unlocked.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not create the password vault.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleUnlockVault = async () => {
    setErrorMessage(null);
    setStatusMessage(null);
    setBusyAction('unlock-vault');
    try {
      await onUnlockVault(unlockPassword);
      setUnlockPassword('');
      setStatusMessage('Password vault unlocked.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not unlock the password vault.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleSavePassword = async () => {
    if (!currentOrigin) {
      setErrorMessage('Open an http(s) website before saving a password.');
      return;
    }

    setErrorMessage(null);
    setStatusMessage(null);
    setBusyAction('save-password');
    try {
      await onSavePassword({
        origin: currentOrigin,
        username,
        password,
        label,
      });
      setUsername('');
      setPassword('');
      setStatusMessage(`Saved credentials for ${getHostname(currentOrigin)}.`);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not save the password.');
    } finally {
      setBusyAction(null);
    }
  };

  const copyText = async (value: string, labelText: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setStatusMessage(`${labelText} copied to clipboard.`);
      setErrorMessage(null);
    } catch {
      setErrorMessage(`Could not copy ${labelText.toLowerCase()}.`);
    }
  };

  const fillEntry = async (entry: BrowserSavedPassword) => {
    setErrorMessage(null);
    setStatusMessage(null);
    setBusyAction(`fill-${entry.id}`);
    try {
      const filled = await onFillPassword(entry);
      if (!filled) {
        setErrorMessage('No compatible login fields were detected on the current page.');
        return;
      }
      setStatusMessage(`Filled credentials for ${getHostname(entry.origin)}.`);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not fill the current page.');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl overflow-hidden p-0" portalContainer={portalContainer}>
        <DialogTitle className="sr-only">Password manager</DialogTitle>
        <DialogDescription className="sr-only">
          Save, unlock, fill, and manage site passwords inside Navigator.
        </DialogDescription>
        <div className="flex h-[76vh] flex-col">
          <div className="border-b border-border/70 px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xl font-semibold tracking-tight">Password Manager</div>
                <p className="mt-1 text-sm text-muted-foreground">Navigator stores saved passwords inside a locally encrypted vault.</p>
              </div>
              {hasPasswordVault && isPasswordVaultUnlocked ? (
                <Button type="button" variant="outline" onClick={onLockVault}>
                  <Lock className="mr-2 h-4 w-4" />
                  Lock vault
                </Button>
              ) : null}
            </div>
            {statusMessage ? <p className="mt-3 text-sm text-primary">{statusMessage}</p> : null}
            {errorMessage ? <p className="mt-3 text-sm text-destructive">{errorMessage}</p> : null}
          </div>

          {!hasPasswordVault ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <Card className="w-full max-w-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ShieldCheck className="h-5 w-5" />
                    Create your vault
                  </CardTitle>
                  <CardDescription>Choose a master password to encrypt all saved site credentials on this device.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="vault-master-password">Master password</Label>
                    <Input
                      id="vault-master-password"
                      type="password"
                      value={setupPassword}
                      onChange={(event) => setSetupPassword(event.target.value)}
                      placeholder="At least 8 characters"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vault-master-password-confirm">Confirm password</Label>
                    <Input
                      id="vault-master-password-confirm"
                      type="password"
                      value={setupPasswordConfirm}
                      onChange={(event) => setSetupPasswordConfirm(event.target.value)}
                    />
                  </div>
                  <Button type="button" onClick={handleCreateVault} disabled={busyAction === 'create-vault'}>
                    <KeyRound className="mr-2 h-4 w-4" />
                    {busyAction === 'create-vault' ? 'Creating vault…' : 'Create vault'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : !isPasswordVaultUnlocked ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <Card className="w-full max-w-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Lock className="h-5 w-5" />
                    Unlock your vault
                  </CardTitle>
                  <CardDescription>Enter your master password to reveal saved credentials for this session.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="vault-unlock-password">Master password</Label>
                    <Input
                      id="vault-unlock-password"
                      type="password"
                      value={unlockPassword}
                      onChange={(event) => setUnlockPassword(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleUnlockVault().catch(() => {});
                        }
                      }}
                    />
                  </div>
                  <Button type="button" onClick={handleUnlockVault} disabled={busyAction === 'unlock-vault'}>
                    {busyAction === 'unlock-vault' ? 'Unlocking…' : 'Unlock vault'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="grid flex-1 gap-6 overflow-hidden p-6 md:grid-cols-[340px_minmax(0,1fr)]">
              <Card className="flex min-h-0 flex-col">
                <CardHeader>
                  <CardTitle className="text-lg">Save current site</CardTitle>
                  <CardDescription>
                    {currentOrigin ? `Store credentials for ${getHostname(currentOrigin)}.` : 'Open a website to save credentials for it.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password-entry-label">Label</Label>
                    <Input
                      id="password-entry-label"
                      value={label}
                      onChange={(event) => setLabel(event.target.value)}
                      placeholder="Example: Work account"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password-entry-username">Username or email</Label>
                    <Input
                      id="password-entry-username"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password-entry-password">Password</Label>
                    <Input
                      id="password-entry-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Saved locally in your encrypted vault"
                    />
                  </div>
                  <Button type="button" onClick={handleSavePassword} disabled={!currentOrigin || busyAction === 'save-password'}>
                    {busyAction === 'save-password' ? 'Saving…' : 'Save password'}
                  </Button>

                  {currentSitePasswords.length > 0 ? (
                    <div className="rounded-lg border border-border/70 bg-muted/25 p-3 text-sm text-muted-foreground">
                      <div className="font-medium text-foreground">Saved for this site</div>
                      <div className="mt-2 space-y-2">
                        {currentSitePasswords.map((entry) => (
                          <div key={entry.id} className="flex items-center justify-between gap-2 rounded-md bg-background/70 px-3 py-2">
                            <div className="min-w-0">
                              <div className="truncate font-medium text-foreground">{entry.label}</div>
                              <div className="truncate text-xs">{entry.username}</div>
                            </div>
                            <Button type="button" size="sm" variant="outline" onClick={() => fillEntry(entry).catch(() => {})}>
                              Fill
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card className="flex min-h-0 flex-col">
                <CardHeader>
                  <CardTitle className="text-lg">Saved passwords</CardTitle>
                  <CardDescription>Use, copy, or delete any credentials currently stored in your encrypted vault.</CardDescription>
                </CardHeader>
                <CardContent className="min-h-0 flex-1">
                  {sortedPasswords.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      No passwords saved yet.
                    </div>
                  ) : (
                    <ScrollArea className="h-full pr-3">
                      <div className="space-y-3">
                        {sortedPasswords.map((entry) => (
                          <div key={entry.id} className="rounded-lg border border-border/70 bg-background/70 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="truncate font-medium text-foreground">{entry.label}</div>
                                <div className="truncate text-sm text-muted-foreground">{entry.origin}</div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Delete ${entry.label}`}
                                onClick={() => onDeletePassword(entry.id).catch(() => {})}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                              <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm">
                                <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
                                  <UserRound className="h-4 w-4" />
                                  Username
                                </div>
                                <div className="truncate text-muted-foreground">{entry.username}</div>
                              </div>
                              <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm">
                                <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
                                  <Lock className="h-4 w-4" />
                                  Password
                                </div>
                                <div className="truncate text-muted-foreground">••••••••••••</div>
                              </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                              <Button type="button" size="sm" onClick={() => fillEntry(entry).catch(() => {})} disabled={busyAction === `fill-${entry.id}`}>
                                {busyAction === `fill-${entry.id}` ? 'Filling…' : 'Fill current page'}
                              </Button>
                              <Button type="button" size="sm" variant="outline" onClick={() => copyText(entry.username, 'Username')}>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy username
                              </Button>
                              <Button type="button" size="sm" variant="outline" onClick={() => copyText(entry.password, 'Password')}>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy password
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};